import * as Sentry from '@sentry/react';
import { ClerkProvider, RedirectToSignIn, useAuth } from '@clerk/clerk-react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { setTokenProvider } from './services/api';
import App from './App';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminLayout from './components/admin/AdminLayout';
import AssignmentsManagement from './components/admin/AssignmentsManagement';
import CriteriaManagement from './components/admin/CriteriaManagement';
import PolicyComparison from './components/admin/PolicyComparison';
import DisagreementReview from './components/admin/DisagreementReview';
import EssaysManagement from './components/admin/EssaysManagement';
import GroundTruthCoverage from './components/admin/GroundTruthCoverage';
import LabelsManagement from './components/admin/LabelsManagement';
import MarkingMetrics from './components/admin/MarkingMetrics';
import Moderation from './components/admin/Moderation';
import ModerationPolicySuggestions from './components/admin/ModerationPolicySuggestions';
import OutputReview from './components/admin/OutputReview';
import PolicySuggestions from './components/admin/PolicySuggestions';
import ResolveDisagreementsHub from './components/admin/ResolveDisagreementsHub';
import ReviewedOutputs from './components/admin/ReviewedOutputs';
import UserVsGroundTruthReview from './components/admin/UserVsGroundTruthReview';
import UsersManagement from './components/admin/UsersManagement';
import Hotjar from './components/Hotjar';
import './App.css';

import { IS_DEV_AUTH } from './hooks/useDevAuth';

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    tracesSampleRate: 1,
    sendDefaultPii: false,
  });
}

const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!IS_DEV_AUTH && !PUBLISHABLE_KEY)
  throw new Error(
    'Set REACT_APP_CLERK_PUBLISHABLE_KEY for production or REACT_APP_DEV_USER for dev mode'
  );

function ClerkApiGate({ children }: { children: React.ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  setTokenProvider(({ forceRefresh }) => getToken({ skipCache: forceRefresh }));
  if (!isLoaded) return null;
  if (!isSignedIn) return <RedirectToSignIn />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Hotjar hjid={5351590} hjsv={6} />
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UsersManagement />} />
          <Route path="criteria" element={<CriteriaManagement />} />
          <Route path="policy-comparison" element={<PolicyComparison />} />
          <Route path="transcriptions" element={<EssaysManagement />} />
          <Route path="labels" element={<LabelsManagement />} />
          <Route path="assignments" element={<AssignmentsManagement />} />
          <Route path="ground-truth-coverage" element={<GroundTruthCoverage />} />
          <Route path="marking-metrics" element={<MarkingMetrics />} />
          <Route path="disagreements" element={<ResolveDisagreementsHub />} />
          <Route path="disagreements/consensus-vs-ai" element={<DisagreementReview />} />
          <Route path="disagreements/consensus-vs-human" element={<UserVsGroundTruthReview />} />
          <Route path="output-review" element={<OutputReview />} />
          <Route path="reviewed-outputs" element={<ReviewedOutputs />} />
          <Route path="policy-suggestions" element={<PolicySuggestions />} />
          <Route path="moderation" element={<Moderation />} />
          <Route path="moderation-suggestions" element={<ModerationPolicySuggestions />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

if (IS_DEV_AUTH) {
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
        <AppRoutes />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
} else {
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
        <ClerkProvider publishableKey={PUBLISHABLE_KEY!} afterSignOutUrl="/">
          <ClerkApiGate>
            <AppRoutes />
          </ClerkApiGate>
        </ClerkProvider>
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
}
